/**
 * Risk Score Calculator Service
 * Implements weighted algorithm for calculating likelihood and impact scores
 * Based on FR-1.2 Calculation Logic from requirements
 */

export interface LikelihoodFactors {
  volumePeriod: 'monthly' | 'annual';
  transactionCount: number;
  dollarValue?: number;
  reachScale: number;
  automationLevel: 'manual' | 'semi-automated' | 'fully-automated';
  historicalIncidents: {
    count: number;
    timeframe: string;
  };
  processComplexity?: number; // From existing data (1-5)
  controlEffectiveness?: number; // From existing data (1-5)
}

export interface ImpactFactors {
  financial: number; // 1-5
  reputational: number; // 1-5
  regulatoryCompliance: number; // 1-5
  operational: number; // 1-5
  strategic: number; // 1-5
}

export interface ScoringConfig {
  weights: {
    volume: number;
    automation: number;
    historical: number;
    complexity: number;
    controls: number;
  };
  volumeThresholds: { [key: string]: number };
  automationScoring: {
    'manual': number;
    'semi-automated': number;
    'fully-automated': number;
  };
  historicalThresholds: {
    per_month: { [key: string]: number };
  };
}

export interface CalculationResult {
  score: number;
  confidence: number;
  reasoning: string;
  breakdown?: {
    volume: number;
    automation: number;
    historical: number;
    complexity: number;
    controls: number;
  };
}

export interface ImpactCalculationResult {
  score: number;
  reasoning: string;
  breakdown: {
    financial: number;
    reputational: number;
    regulatoryCompliance: number;
    operational: number;
    strategic: number;
  };
}

export class RiskScoreCalculator {
  private config: ScoringConfig;

  constructor(config?: Partial<ScoringConfig>) {
    // Default configuration aligned with FR-1.2
    this.config = {
      weights: {
        volume: 0.25,
        automation: 0.20,
        historical: 0.30,
        complexity: 0.15,
        controls: 0.10,
        ...config?.weights
      },
      volumeThresholds: {
        '1': 0,
        '2': 100,
        '3': 1000,
        '4': 10000,
        '5': 100000,
        ...config?.volumeThresholds
      },
      automationScoring: {
        'manual': 5,
        'semi-automated': 3,
        'fully-automated': 1,
        ...config?.automationScoring
      },
      historicalThresholds: {
        per_month: {
          '1': 0,
          '2': 0.1,
          '3': 0.5,
          '4': 1.0,
          '5': 2.0,
          ...config?.historicalThresholds?.per_month
        }
      }
    };
  }

  /**
   * Calculate likelihood score based on weighted algorithm
   * FR-1.2: System calculates likelihood score (1-5) based on weighted algorithm
   */
  calculateLikelihoodScore(factors: LikelihoodFactors): CalculationResult {
    console.log('🧮 [RiskScoreCalculator] calculateLikelihoodScore() called');
    console.log('   📥 Input factors:', factors);

    let totalScore = 0;
    const reasons: string[] = [];
    const breakdown: any = {};

    // 1. Volume Score (25% weight)
    // FR-1.2: Volume thresholds: Higher volume = higher likelihood
    console.log('   🔢 Calculating Volume Score (25% weight)...');
    const volumeScore = this.calculateVolumeScore(
      factors.transactionCount || 0,
      factors.volumePeriod || 'monthly'
    );
    breakdown.volume = volumeScore;
    totalScore += volumeScore * this.config.weights.volume;
    console.log(`      ✓ Volume Score: ${volumeScore}/5 × ${this.config.weights.volume} = ${volumeScore * this.config.weights.volume}`);
    reasons.push(
      `Transaction volume of ${(factors.transactionCount || 0).toLocaleString()}/${factors.volumePeriod || 'monthly'} ` +
      `rates as ${this.getScoreLabel(volumeScore)} likelihood (Score: ${volumeScore}/5)`
    );

    // 2. Automation Score (20% weight)
    // FR-1.2: Automation: Manual > Semi-Automated > Fully Automated
    console.log('   🔢 Calculating Automation Score (20% weight)...');
    const automationScore = this.calculateAutomationScore(factors.automationLevel || 'semi-automated');
    breakdown.automation = automationScore;
    totalScore += automationScore * this.config.weights.automation;
    console.log(`      ✓ Automation Score: ${automationScore}/5 × ${this.config.weights.automation} = ${automationScore * this.config.weights.automation}`);
    reasons.push(
      `${this.formatAutomationLevel(factors.automationLevel || 'semi-automated')} process ` +
      `increases risk likelihood (Score: ${automationScore}/5)`
    );

    // 3. Historical Incidents Score (30% weight - highest impact)
    // FR-1.2: Historical incidents: Frequency and severity increase likelihood
    console.log('   🔢 Calculating Historical Incidents Score (30% weight)...');
    const historicalScore = this.calculateHistoricalScore(
      factors.historicalIncidents?.count || 0,
      factors.historicalIncidents?.timeframe || '12 months'
    );
    breakdown.historical = historicalScore;
    totalScore += historicalScore * this.config.weights.historical;
    console.log(`      ✓ Historical Score: ${historicalScore}/5 × ${this.config.weights.historical} = ${historicalScore * this.config.weights.historical}`);

    const incidentCount = factors.historicalIncidents?.count || 0;
    const incidentTimeframe = factors.historicalIncidents?.timeframe || '12 months';

    if (incidentCount > 0) {
      reasons.push(
        `${incidentCount} incident(s) in ${incidentTimeframe} ` +
        `strongly indicates ${this.getScoreLabel(historicalScore)} recurrence risk (Score: ${historicalScore}/5)`
      );
    } else {
      reasons.push(
        `No historical incidents recorded, indicating ${this.getScoreLabel(historicalScore)} likelihood (Score: ${historicalScore}/5)`
      );
    }

    // 4. Process Complexity Score (15% weight)
    // FR-1.2: Process complexity: Higher complexity = higher likelihood (from existing data)
    console.log('   🔢 Calculating Process Complexity Score (15% weight)...');
    const complexityScore = factors.processComplexity || 3;
    breakdown.complexity = complexityScore;
    totalScore += complexityScore * this.config.weights.complexity;
    console.log(`      ✓ Complexity Score: ${complexityScore}/5 × ${this.config.weights.complexity} = ${complexityScore * this.config.weights.complexity}`);
    reasons.push(
      `Process complexity rated at ${complexityScore}/5`
    );

    // 5. Control Effectiveness Score (10% weight)
    // FR-1.2: Control effectiveness: Weaker controls = higher likelihood (from existing data)
    console.log('   🔢 Calculating Control Effectiveness Score (10% weight)...');
    const controlScore = this.calculateControlScore(factors.controlEffectiveness || 3);
    breakdown.controls = controlScore;
    totalScore += controlScore * this.config.weights.controls;
    console.log(`      ✓ Control Score: ${controlScore}/5 (inverted from ${factors.controlEffectiveness || 3}/5) × ${this.config.weights.controls} = ${controlScore * this.config.weights.controls}`);
    reasons.push(
      `Control effectiveness of ${factors.controlEffectiveness || 3}/5 ` +
      `results in inverted risk score of ${controlScore}/5`
    );

    console.log('   ➕ Total Score (before normalization):', totalScore);

    // Normalize to 1-5 scale
    const finalScore = Math.round(Math.min(5, Math.max(1, totalScore)));
    console.log('   ✅ Final Likelihood Score (normalized):', finalScore);

    // Calculate confidence based on data completeness
    console.log('   📊 Calculating confidence...');
    const confidence = this.calculateConfidence(factors);
    console.log('   ✅ Confidence:', (confidence * 100).toFixed(0) + '%');

    const result = {
      score: finalScore,
      confidence,
      reasoning: this.generateLikelihoodReasoning(finalScore, reasons, confidence, breakdown),
      breakdown
    };

    console.log('✅ [RiskScoreCalculator] Likelihood calculation complete:', result);
    return result;
  }

  /**
   * Calculate impact score from multiple dimensions
   */
  calculateImpactScore(factors: ImpactFactors): ImpactCalculationResult {
    console.log('🧮 [RiskScoreCalculator] calculateImpactScore() called');
    console.log('   📥 Input factors:', factors);

    const scores = [
      factors.financial,
      factors.reputational,
      factors.regulatoryCompliance,
      factors.operational,
      factors.strategic
    ];

    console.log('   📊 Individual scores:', {
      financial: factors.financial,
      reputational: factors.reputational,
      regulatoryCompliance: factors.regulatoryCompliance,
      operational: factors.operational,
      strategic: factors.strategic
    });

    // Simple average of all impact dimensions
    const avgScore = scores.reduce((sum, s) => sum + s, 0) / scores.length;
    console.log('   ➕ Sum of all scores:', scores.reduce((sum, s) => sum + s, 0));
    console.log('   ➗ Average score:', avgScore.toFixed(2));

    const finalScore = Math.round(avgScore);
    console.log('   ✅ Final Impact Score (rounded):', finalScore);

    const impactLabels = [
      `Financial: ${this.getImpactLabel(factors.financial)}`,
      `Reputational: ${this.getImpactLabel(factors.reputational)}`,
      `Regulatory/Compliance: ${this.getImpactLabel(factors.regulatoryCompliance)}`,
      `Operational: ${this.getImpactLabel(factors.operational)}`,
      `Strategic: ${this.getImpactLabel(factors.strategic)}`
    ];

    const result = {
      score: finalScore,
      reasoning: `Combined impact assessment across 5 dimensions:\n\n${impactLabels.join('\n')}\n\nOverall Impact Rating: ${this.getImpactLabel(finalScore)} (${finalScore}/5)`,
      breakdown: {
        financial: factors.financial,
        reputational: factors.reputational,
        regulatoryCompliance: factors.regulatoryCompliance,
        operational: factors.operational,
        strategic: factors.strategic
      }
    };

    console.log('✅ [RiskScoreCalculator] Impact calculation complete:', result);
    return result;
  }

  /**
   * Calculate volume score based on thresholds
   * FR-1.2: Higher volume = higher likelihood
   */
  private calculateVolumeScore(count: number, period: string): number {
    // Handle null/undefined
    const safeCount = count || 0;
    const safePeriod = period || 'monthly';

    // Normalize to monthly if annual
    const monthlyCount = safePeriod === 'annual' ? safeCount / 12 : safeCount;

    // Map to thresholds (5 = highest risk)
    if (monthlyCount >= this.config.volumeThresholds['5']) return 5;
    if (monthlyCount >= this.config.volumeThresholds['4']) return 4;
    if (monthlyCount >= this.config.volumeThresholds['3']) return 3;
    if (monthlyCount >= this.config.volumeThresholds['2']) return 2;
    return 1;
  }

  /**
   * Calculate automation score
   * FR-1.2: Manual > Semi-Automated > Fully Automated (manual = highest risk)
   */
  private calculateAutomationScore(level: string): number {
    const safeLevel = level || 'semi-automated';
    return this.config.automationScoring[safeLevel as keyof typeof this.config.automationScoring] || 3;
  }

  /**
   * Calculate historical incidents score
   * FR-1.2: Frequency and severity increase likelihood
   */
  private calculateHistoricalScore(count: number, timeframe: string): number {
    // Handle null/undefined
    const safeCount = count || 0;
    const safeTimeframe = timeframe || '12 months';

    // Extract months from timeframe (e.g., "12 months" -> 12)
    const months = parseInt(safeTimeframe) || 12;
    const monthlyRate = safeCount / months;

    // Map to thresholds
    if (monthlyRate >= this.config.historicalThresholds.per_month['5']) return 5;
    if (monthlyRate >= this.config.historicalThresholds.per_month['4']) return 4;
    if (monthlyRate >= this.config.historicalThresholds.per_month['3']) return 3;
    if (monthlyRate >= this.config.historicalThresholds.per_month['2']) return 2;
    return 1;
  }

  /**
   * Calculate control effectiveness score
   * FR-1.2: Weaker controls = higher likelihood
   * Inverts the effectiveness rating (5 = strong controls = 1 risk, 1 = weak controls = 5 risk)
   */
  private calculateControlScore(effectiveness: number): number {
    return 6 - effectiveness;
  }

  /**
   * Calculate confidence based on data completeness
   * FR-1.2: Display calculated score with confidence indicator
   */
  private calculateConfidence(factors: LikelihoodFactors): number {
    let completeness = 0;
    const totalFields = 8;

    if (factors.transactionCount > 0) completeness++;
    if (factors.dollarValue && factors.dollarValue > 0) completeness++;
    if (factors.reachScale > 0) completeness++;
    if (factors.automationLevel) completeness++;
    if (factors.historicalIncidents.count >= 0) completeness++;
    if (factors.historicalIncidents.timeframe) completeness++;
    if (factors.processComplexity) completeness++;
    if (factors.controlEffectiveness) completeness++;

    return completeness / totalFields;
  }

  /**
   * Generate human-readable reasoning
   * FR-1.2: Show AI reasoning in plain language
   */
  private generateLikelihoodReasoning(
    score: number,
    reasons: string[],
    confidence: number,
    breakdown: any
  ): string {
    return `Based on weighted analysis, this risk has a calculated likelihood score of ${score}/5 - ${this.getScoreLabel(score)}.

📊 Scoring Breakdown:
• Volume Factor (25%): ${breakdown.volume}/5
• Automation Factor (20%): ${breakdown.automation}/5
• Historical Incidents (30%): ${breakdown.historical}/5
• Process Complexity (15%): ${breakdown.complexity}/5
• Control Effectiveness (10%): ${breakdown.controls}/5

🔍 Key Contributing Factors:
${reasons.map(r => `• ${r}`).join('\n')}

📈 Data Confidence: ${(confidence * 100).toFixed(0)}% (based on ${Math.round(confidence * 8)}/8 factors provided)

This score is calculated using a weighted algorithm that considers volume thresholds, automation levels, historical incident patterns, process complexity, and control effectiveness.`;
  }

  /**
   * Get likelihood score label
   */
  private getScoreLabel(score: number): string {
    const labels: { [key: number]: string } = {
      1: 'Very Low',
      2: 'Low',
      3: 'Medium',
      4: 'High',
      5: 'Very High'
    };
    return labels[Math.round(score)] || 'Medium';
  }

  /**
   * Get impact score label
   */
  private getImpactLabel(score: number): string {
    const labels: { [key: number]: string } = {
      1: 'Minimal',
      2: 'Minor',
      3: 'Moderate',
      4: 'Major',
      5: 'Severe'
    };
    return labels[score] || 'Moderate';
  }

  /**
   * Format automation level for display
   */
  private formatAutomationLevel(level: string): string {
    const safeLevel = level || 'semi-automated';
    const formats: { [key: string]: string } = {
      'manual': 'Manual',
      'semi-automated': 'Semi-Automated',
      'fully-automated': 'Fully Automated'
    };
    return formats[safeLevel] || safeLevel;
  }

  /**
   * Get final risk level based on likelihood × impact
   */
  public getFinalRiskLevel(likelihoodScore: number, impactScore: number): {
    score: number;
    level: string;
    color: string;
  } {
    const score = likelihoodScore * impactScore;

    let level = 'Low';
    let color = 'green';

    if (score >= 20) {
      level = 'Critical';
      color = 'red';
    } else if (score >= 15) {
      level = 'High';
      color = 'orange';
    } else if (score >= 8) {
      level = 'Medium';
      color = 'yellow';
    } else {
      level = 'Low';
      color = 'green';
    }

    return { score, level, color };
  }

  /**
   * Validate input factors
   */
  public validateLikelihoodFactors(factors: Partial<LikelihoodFactors>): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!factors.volumePeriod) {
      errors.push('Volume period is required');
    }

    if (!factors.transactionCount || factors.transactionCount < 0) {
      errors.push('Transaction count must be a positive number');
    }

    if (!factors.reachScale || factors.reachScale < 0) {
      errors.push('Reach/scale must be a positive number');
    }

    if (!factors.automationLevel) {
      errors.push('Automation level is required');
    }

    if (factors.historicalIncidents) {
      if (factors.historicalIncidents.count < 0) {
        errors.push('Historical incidents count cannot be negative');
      }
      if (!factors.historicalIncidents.timeframe) {
        errors.push('Historical incidents timeframe is required');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate impact factors
   */
  public validateImpactFactors(factors: Partial<ImpactFactors>): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    const requiredFields: (keyof ImpactFactors)[] = [
      'financial',
      'reputational',
      'regulatoryCompliance',
      'operational',
      'strategic'
    ];

    requiredFields.forEach(field => {
      const value = factors[field];
      if (!value || value < 1 || value > 5) {
        errors.push(`${field} must be between 1 and 5`);
      }
    });

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance with default config
export const defaultRiskCalculator = new RiskScoreCalculator();
